import { useCallback, useMemo, useRef } from 'react';

export type UseFormNavOptions<T extends HTMLElement = HTMLElement> = {
  ref?: React.RefObject<T>;
  selectors?: string[];
  nextKeys?: string[];
  prevKeys?: string[];
  wrap?: boolean;
  sortByTabIndex?: boolean;
  skipAmount?: number;
  allowDropdownEnterSelect?: boolean;
};

export const isOpenCombobox = (target: HTMLElement) => {
  const openOverlay = target.closest<HTMLElement>(
    '.p-dropdown-panel:not(.p-hidden), .p-autocomplete-panel:not(.p-hidden), .p-multiselect-panel:not(.p-hidden)',
  );
  if (openOverlay) return true;

  const comboboxOwner = target.closest<HTMLElement>(
    '.p-dropdown, .p-autocomplete, .p-multiselect',
  );
  const combobox =
    (target.matches('[role="combobox"]') ? target : null) ??
    target.closest<HTMLElement>('[role="combobox"]') ??
    (comboboxOwner?.matches('[role="combobox"]') ? comboboxOwner : null) ??
    target.querySelector<HTMLElement>('[role="combobox"]') ??
    comboboxOwner?.querySelector<HTMLElement>('[role="combobox"]');
  const expandedListboxControl = comboboxOwner?.querySelector<HTMLElement>(
    '[aria-haspopup="listbox"][aria-expanded="true"]',
  );

  return (
    combobox?.getAttribute('aria-expanded') === 'true' ||
    expandedListboxControl !== null
  );
};

const isInDropdown = (target: HTMLElement) =>
  target.closest(
    '.p-dropdown, .p-autocomplete, .p-multiselect, .p-dropdown-panel, .p-autocomplete-panel, .p-multiselect-panel',
  ) !== null ||
  target.getAttribute('role') === 'combobox' ||
  target.getAttribute('aria-haspopup') === 'listbox';

export function useFormNav<T extends HTMLElement = HTMLElement>(
  opts: UseFormNavOptions<T> = {},
) {
  const {
    ref: externalRef,
    selectors = [
      'input:not([type="hidden"]):not([disabled])',
      'select:not([disabled])',
      '.p-dropdown:not(.p-disabled) input[role="combobox"]',
      'textarea:not([disabled])',
      'button:not([disabled])[tabindex]',
    ],
    nextKeys = ['Tab', 'ArrowRight'],
    prevKeys = ['ArrowLeft'],
    wrap = true,
    sortByTabIndex = false,
    skipAmount = 2,
    allowDropdownEnterSelect = false,
  } = opts;

  const internalRef = useRef<T>(null);
  const ref = externalRef ?? internalRef;

  const focusSelector = useMemo(() => selectors.join(','), [selectors]);

  const selectOnFocus = useCallback(
    (e?: React.FocusEvent<HTMLElement> | FocusEvent | any) => {
      const el: any =
        e?.currentTarget ??
        e?.target ??
        (typeof document !== 'undefined'
          ? (document.activeElement as any)
          : null);

      if (el && typeof el.select === 'function') {
        try {
          el.select();
        } catch {
          // Ignore errors if select is not available
        }
      }
    },
    [],
  );

  const onKeyDownCapture = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      const key = e.key;
      const isShiftTab = key === 'Tab' && e.shiftKey;
      const isTab = key === 'Tab' && !e.shiftKey;
      const isNextKey = nextKeys.includes(key) && !isShiftTab;
      const isPrevKey = prevKeys.includes(key) || isShiftTab;
      if (!isNextKey && !isPrevKey) return;

      const target = e.target as HTMLElement;
      const tagName = target.tagName.toLowerCase();

      if (tagName === 'textarea') return;

      const isDropdown =
        target.closest('.p-dropdown') ||
        target.getAttribute('role') === 'combobox' ||
        tagName === 'select';

      if (isDropdown) {
        if (!isNextKey) return;
        e.preventDefault();
        e.stopPropagation();
      } else {
        e.preventDefault();
      }

      const container = ref.current;
      if (!container || typeof container.querySelectorAll !== 'function')
        return;

      let focusables = Array.from(
        container.querySelectorAll<HTMLElement>(focusSelector),
      );

      if (sortByTabIndex) {
        focusables = focusables.sort((a, b) => {
          const ta = a.tabIndex === 0 ? Number.MAX_SAFE_INTEGER : a.tabIndex;
          const tb = b.tabIndex === 0 ? Number.MAX_SAFE_INTEGER : b.tabIndex;
          return ta - tb;
        });
      }

      const idx = focusables.findIndex(
        (el) => el === target || el.contains(target),
      );
      if (idx < 0) return;

      const delta = isNextKey ? 1 : -1;
      let next = idx + delta;

      if (wrap) {
        if (next < 0) next = focusables.length - 1;
        if (next >= focusables.length) next = 0;
      } else {
        if (next < 0 || next >= focusables.length) return;
      }

      focusables[next]?.focus();
    },
    [focusSelector, nextKeys, prevKeys, ref, sortByTabIndex, wrap],
  );

  const handleEnterKeyAdvance = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key !== 'Enter') return;

      const target = e.target as HTMLElement;

      if (target.hasAttribute('data-skip-enter-advance')) {
        return;
      }

      if (allowDropdownEnterSelect) {
        if (isInDropdown(target) && isOpenCombobox(target)) {
          return;
        }
      }

      e.preventDefault();
      e.stopPropagation();

      const container = ref.current;
      if (!container || typeof container.querySelectorAll !== 'function')
        return;

      const focusSelector = [
        'input:not([type="hidden"]):not([disabled]):not([readonly])',
        'select:not([disabled])',
        '.p-dropdown:not(.p-disabled) input',
        '.p-dropdown:not(.p-disabled) input[role="combobox"]',
        '.p-dropdown:not(.p-disabled) .p-dropdown-label',
        '.p-autocomplete:not(.p-disabled) input',
        '.p-calendar:not(.p-disabled) input',
        '.p-inputmask:not(.p-disabled) input',
        '.p-inputnumber:not(.p-disabled) input',
        '.p-selectbutton:not(.p-disabled) .p-button:first-child',
        '.p-nucleus:not(.p-disabled) .p-button:first-child',
        'textarea:not([disabled]):not([readonly])',
        'button:not([disabled])[tabindex]',
      ].join(',');

      const focusables = Array.from(
        container.querySelectorAll<HTMLElement>(focusSelector),
      ).sort((a, b) => {
        const ta = a.tabIndex === 0 ? Number.MAX_SAFE_INTEGER : a.tabIndex;
        const tb = b.tabIndex === 0 ? Number.MAX_SAFE_INTEGER : b.tabIndex;
        return ta - tb;
      });

      const currentIndex = focusables.findIndex(
        (el) =>
          el === document.activeElement ||
          el.contains(document.activeElement as Node),
      );

      if (currentIndex >= 0) {
        const isShiftPressed = e.shiftKey;
        const direction = isShiftPressed ? -skipAmount : skipAmount;
        const nextIndex =
          (currentIndex + direction + focusables.length) % focusables.length;
        focusables[nextIndex]?.focus();
      }
    },
    [ref, skipAmount, allowDropdownEnterSelect],
  );

  return {
    ref,
    onKeyDownCapture,
    selectOnFocus,
    handleEnterKeyAdvance,
  };
}
